import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { useProductsApi } from '../apis/products-apis';
import { Playbook } from '../types';
import { useToast } from "../components/ui/Toast";
import PlaybookForm, { PlaybookFormRef } from './PlaybookForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigationWarning } from '../hooks/useNavigationWarning';
import { Spinner } from "../components/ui/Spinner";
import { Loader2 } from 'lucide-react';

const emptyPlaybook: Playbook = {
  playbookId: '',
  playbookName: '',
  playbookProductName: '',
  playbookDescription: '',
  playbookWorkflow: '',
  playbookSpecialInstructions: '',
  playbookApprovalNotes: '',
  playbookApprovalTools: [],
  playbookActiveTools: [],
  playbookHelperAgents: [],
  playbookChatType: '',
  playbookModel: 'gpt-4o',
  playbookTags: [],
  tags: [],
  isCore: false,
  accessControl: {
    viewAccessType: 'Owner Only',
    viewAllowedUsers: [],
    editAccessType: 'Owner Only',
    editAllowedUsers: []
  },
  ownerId: ''
};

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
      <span className="text-lg font-medium">Saving Playbook... Please wait</span>
    </div>
  </div>
);

export const AddPlaybookPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const formRef = useRef<PlaybookFormRef>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [existingPlaybooks, setExistingPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPlaybook, setNewPlaybook] = useState<Playbook>(emptyPlaybook);
  const playbooksApi = usePlaybooksApi();
  const productsApi = useProductsApi();
  
  // Check if any fields have been modified from their initial empty state
  const hasUnsavedChanges = !isSaving && Object.entries(newPlaybook).some(([key, value]) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'string') {
      return value !== emptyPlaybook[key as keyof Playbook];
    }
    return false;
  });

  const {
    showDialog: showNavigationWarning,
    handleNavigationAttempt,
    handleConfirmNavigation,
    handleCancelNavigation
  } = useNavigationWarning({
    shouldWarn: hasUnsavedChanges,
    onConfirm: () => {
      setNewPlaybook(emptyPlaybook);
      setIsSaving(false);
    }
  });

  // Handle browser back button and page refresh/close
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchExistingPlaybooks = async () => {
      try {
        const getAllPlaybooks = true;
        const playbooks = await playbooksApi.getPlaybooks(getAllPlaybooks);
        setExistingPlaybooks(playbooks);
      } catch (error) {
        console.error('Error fetching playbooks:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch existing playbooks",
          variant: "destructive",
          duration: 4000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchExistingPlaybooks();
  }, [addToast]);

  const handleSubmit = async () => {
    const isValid = await formRef.current?.validateForm() ?? false;
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    try {
      setIsSaving(true);
      
      // Handle product creation if needed
      if (newPlaybook.playbookProductName) {
        try {
          // This will either find the existing product or create a new one
          await productsApi.findOrCreateProduct(newPlaybook.playbookProductName);
        } catch (productError) {
          console.error('Error handling product:', productError);
          addToast({
            title: "Warning",
            description: "There was an issue with the product, but the playbook creation will proceed",
            variant: "default",
            duration: 4000
          });
          // Continue with playbook creation despite product issues
        }
      }
      
      // Create the playbook
      await playbooksApi.createPlaybook(newPlaybook);
      
      addToast({
        title: "Success",
        description: "Playbook " + newPlaybook.playbookName + " has been successfully created",
        variant: "default",
        duration: 4000
      });

      navigate('/studio/playbooks');
    } catch (error) {
      console.error('Error creating playbook:', error);
      setIsSaving(false);
      
      addToast({
        title: "Error",
        description: "Failed to create playbook: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-sky-500" />
        <p className="text-center text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      {isSaving && <LoadingOverlay />}
      
      <PlaybookForm
        ref={formRef}
        playbook={newPlaybook}
        onPlaybookChange={setNewPlaybook}
        onSubmit={handleSubmit}
        isEditing={false}
        setIsReadOnly={() => {}}
        existingPlaybooks={existingPlaybooks}
        hasUnsavedChanges={hasUnsavedChanges}
        onCancel={() => navigate('/studio/playbooks')}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default AddPlaybookPage;